"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import type {AssistantContentPart} from "@/types/chatMessages";
import {CitationSource} from "@/types/ask";
import {ContentPartGroup, groupContentParts} from "@/utils/chat/chatViewUtils";
import AskNavigationBubble from "./AskNavigationBubble";
import AskText from "./AskText";
import AskToolCall from "./AskToolCall";
import {isCodeExecutionTool} from "./toolCallUtils";
import ReasoningBlock from "../chat/ReasoningBlock";
import ToolActivityGroup from "./ToolActivityGroup";

type AssistantContentPartsProps = Readonly<{
   contentParts: AssistantContentPart[];
   messageIndex: number;
   isUser: boolean;
   isStreaming: boolean;
   citations?: CitationSource[];
   textVariant?: "default" | "v1";
   preserveEmoji?: boolean;
   /** Forwarded to navigation so the same conversation can sit beside the opened page. */
   conversationId?: string;
}>;

type ThinkingPart = Extract<AssistantContentPart, {type: "thinking"}>;
type TextPart = Extract<AssistantContentPart, {type: "text"}>;
type FunctionCallPart = Extract<AssistantContentPart, {type: "functionCall"}>;
type NavigationPart = Extract<AssistantContentPart, {type: "navigation"}>;

interface ContentPartListOptions {
   messageIndex: number;
   isUser: boolean;
   isStreaming: boolean;
   citations: CitationSource[] | undefined;
   /** Absolute index of the last part in the full contentParts array. */
   lastPartIdx: number;
   insideActivity?: boolean;
   textVariant: "default" | "v1";
   preserveEmoji: boolean;
   conversationId?: string;
}

/**
 * Keys use the part's absolute index in the full contentParts array (via
 * `group.startIdx`) so React identity stays stable when the activity/answer
 * split moves — e.g. a new tool arrives after answer text has already started.
 */
const partKey = (
   type: string,
   messageIndex: number,
   absoluteIdx: number,
   callId?: string
) => callId || `${type}-${messageIndex}-${absoluteIdx}`;

const renderThinkingPart = (
   part: ThinkingPart,
   absoluteIdx: number,
   {messageIndex, isStreaming, lastPartIdx}: ContentPartListOptions
) => (
   <ReasoningBlock
      key={partKey("thinking", messageIndex, absoluteIdx)}
      content={part.content}
      isStreaming={isStreaming && absoluteIdx === lastPartIdx}
   />
);

const renderTextPart = (
   part: TextPart,
   absoluteIdx: number,
   {
      messageIndex,
      isUser,
      citations,
      insideActivity = false,
      textVariant,
      preserveEmoji,
   }: ContentPartListOptions
) => (
   <div
      key={partKey("text", messageIndex, absoluteIdx)}
      className={clsx(
         !insideActivity && absoluteIdx > 0 && "mt-3",
         insideActivity && "flex items-start gap-2 text-500"
      )}
   >
      {insideActivity && (
         <Icon icon="message-lines" className="mt-1 h-3 w-3 flex-shrink-0 text-300" />
      )}
      <div className={clsx("min-w-0", insideActivity && "text-[13px] leading-5")}>
         <AskText
            text={part.content}
            isMarkDown={!isUser}
            variant={textVariant}
            citations={citations}
            preserveEmoji={preserveEmoji}
         />
      </div>
   </div>
);

const isDocsToolName = (functionName: string) =>
   functionName === "search_docs" || functionName === "read_doc_page";

const renderFunctionCallGroup = (
   parts: FunctionCallPart[],
   absoluteIdx: number,
   {
      messageIndex,
      isStreaming,
      insideActivity = false,
   }: ContentPartListOptions
) => {
   // When the stream has ended, incomplete tools will never get a result —
   // treat them as settled so step UI stops shimmering / force-opening.
   const allCompleted = !isStreaming || parts.every((p) => p.isCompleted);
   const stableKey = partKey(
      "fc",
      messageIndex,
      absoluteIdx,
      parts[0].data.callId
   );
   const displayPart = parts.findLast((p) => p.isCompleted) ?? parts[0];
   const countOverride = allCompleted && parts.length > 1 ? parts.length : undefined;
   const steps = parts.map((part) => ({
      data: part.data,
      isCompleted: part.isCompleted || !isStreaming,
      result: part.result,
   }));
   const isCodeGroup = parts.every((part) =>
      isCodeExecutionTool(part.data.functionName ?? "")
   );

   return (
      <div
         key={stableKey}
         className={clsx(
            !insideActivity && absoluteIdx > 0 && "mt-3",
            !isCodeGroup && parts.length > 1 && "space-y-2.5"
         )}
      >
         {isCodeGroup || parts.length === 1 ? (
            <AskToolCall
               data={displayPart.data}
               isCompleted={allCompleted}
               result={displayPart.result}
               count={countOverride}
               steps={steps}
            />
         ) : (
            steps.map((step) => (
               <AskToolCall
                  key={step.data.callId}
                  data={step.data}
                  isCompleted={step.isCompleted}
                  result={step.result}
               />
            ))
         )}
      </div>
   );
};

const renderNavigationPart = (
   part: NavigationPart,
   absoluteIdx: number,
   {messageIndex, isUser, isStreaming, conversationId}: ContentPartListOptions
) => (
   <div
      key={partKey("nav", messageIndex, absoluteIdx)}
      className={absoluteIdx > 0 ? "mt-3" : ""}
   >
      <AskNavigationBubble
         navigation={part.data}
         isUser={isUser}
         autoNavigate={!isUser && !isStreaming}
         conversationId={conversationId}
      />
   </div>
);

const renderGroup = (group: ContentPartGroup, options: ContentPartListOptions) => {
   const absoluteIdx = group.startIdx;

   if (group.type === "thinking") {
      return renderThinkingPart(group.parts[0] as ThinkingPart, absoluteIdx, options);
   }
   if (group.type === "text") {
      return renderTextPart(group.parts[0] as TextPart, absoluteIdx, options);
   }
   if (group.type === "functionCallGroup") {
      return renderFunctionCallGroup(group.parts as FunctionCallPart[], absoluteIdx, options);
   }
   if (group.type === "navigation") {
      return renderNavigationPart(group.parts[0] as NavigationPart, absoluteIdx, options);
   }
   return null;
};

const renderGroups = (groups: ContentPartGroup[], options: ContentPartListOptions) =>
   groups.map((group) => renderGroup(group, options));

/**
 * Renders ordered assistant content parts, collapsing the working trace
 * (text + tools up through the last tool call) into ToolActivityGroup and
 * leaving post-tool text visible as the answer.
 */
export default function AssistantContentParts({
   contentParts,
   messageIndex,
   isUser,
   isStreaming,
   citations,
   textVariant,
   preserveEmoji = false,
   conversationId,
}: AssistantContentPartsProps) {
   const {t} = useTranslation("ask");
   const lastToolIndex = isUser
      ? -1
      : contentParts.findLastIndex((part) => part.type === "functionCall");

   const options: ContentPartListOptions = {
      messageIndex,
      isUser,
      isStreaming,
      citations,
      lastPartIdx: contentParts.length - 1,
      textVariant: textVariant ?? (isUser ? "default" : "v1"),
      preserveEmoji,
      conversationId,
   };

   // Group the full array so `startIdx` is always an absolute contentParts
   // index — never a position within a derived activity/answer slice.
   const groups = groupContentParts(contentParts);

   if (lastToolIndex === -1) {
      return <>{renderGroups(groups, options)}</>;
   }

   const toolParts = contentParts.filter(
      (part): part is AssistantContentPart & {type: "functionCall"} =>
         part.type === "functionCall"
   );

   if (toolParts.length === 1) {
      return <>{renderGroups(groups, options)}</>;
   }

   const activityGroups = groups.filter((group) => group.startIdx <= lastToolIndex);
   const answerGroups = groups.filter((group) => group.startIdx > lastToolIndex);
   const messageCount = contentParts
      .slice(0, lastToolIndex + 1)
      .filter((part) => part.type === "text").length;
   // Incomplete tools only count as "active" while this message is still
   // streaming. After cancel/error/finish, no function_result will arrive, so
   // treating them as active would shimmer and force-open the group forever.
   const isActivityActive =
      isStreaming && toolParts.some((part) => !part.isCompleted);
   const allDocsTools = toolParts.every((part) =>
      isDocsToolName(part.data.functionName ?? "")
   );
   const docsSummary = allDocsTools
      ? isActivityActive
         ? t("tools.phrases.searchingDocs")
         : t("tools.phrases.searchedDocs")
      : undefined;

   return (
      <>
         <ToolActivityGroup
            toolCount={toolParts.length}
            messageCount={messageCount}
            isActive={isActivityActive}
            summaryOverride={docsSummary}
         >
            {renderGroups(activityGroups, {...options, insideActivity: true})}
         </ToolActivityGroup>
         {answerGroups.length > 0 && renderGroups(answerGroups, options)}
      </>
   );
}
